/**
 * Google Tag Manager / GA4 analytics for the Jack Wolfskin theme.
 * Reads page, shop, and cart config from `window.GTMConfig` (set in `snippets/gtm-valantic.liquid`).
 * Logged-in `accountId` come from `#gtm-customer-ids` (JSON), consumed once for `pageview`, not stored globally.
 * Loaded as `type="module"` with `fetchpriority="low"` so parsing does not block the main thread.
 *
 * Voyado-owned events (`filter`, `sorting`, `onsite_search`, `search_flyout_*`, etc.) live in `src/voyado-app/`.
 *
 * @module gtm-valantic
 */

const cfg = /** @type {import('./gtm-valantic').GTMConfig} */ (window.GTMConfig);
if (!cfg) throw new Error('[GTM] window.GTMConfig not found');

/** @constant {string} */
const GTM_AFFILIATION = cfg.affiliation;

/** @constant {{ item_list_id: string, item_list_name: string }} */
const CART_LIST_CONTEXT = cfg.cartListContext;

/** Active shop currency (Shopify global overrides Liquid default). */
const CURRENCY = window?.Shopify?.currency?.active ?? cfg.currency;

/** Shopify `template.name` from Liquid (e.g. `product`, `cart`, `index`). */
const PAGE_TYPE = cfg.pageType;

/**
 * Reads logged-in customer ids from `#gtm-customer-ids`, removes the node (not kept on `window`).
 * @returns {{ accountId: string}}
 */
function consumeCustomerIds() {
  const el = document.getElementById('gtm-customer-ids');
  if (!el) return {accountId: ''};

  let data;
  try {
    data = JSON.parse(el.textContent || '{}');
  } catch {
    console.warn('[GTM] Invalid customer ids JSON');
    el.remove();
    return {accountId: ''};
  }

  el.remove();
  return {
    accountId: String(data.accountId ?? '')
  };
}

/** One-time customer ids for the initial pageview (not on `window.GTMConfig`). */
const customerIds = consumeCustomerIds();

/**
 * Maps Shopify template names to GA4 `page.type` values.
 * @type {Record<string, string>}
 */
const PAGE_TYPE_MAP = {
    404: '404',
    article: 'article',
    blog: 'blog',
    cart: 'cart',
    collection: 'categorypage',
    gift_card: 'gift_card',
    index: 'homepage',
    product: 'product',
    search: 'searchresults',
    'customers/login': 'login',
    'customers/register': 'sign_up',
};

/**
 * Selectors used to find clickable controls inside popups for `popup_cta`.
 * @constant {string[]}
 */
const BUTTON_SELECTORS = [
    'button',
    '.text-button',
    '[role="button"]',
    'input[type="button"]',
    'input[type="submit"]',
];

/** Matches Shopify Cart API mutation endpoints intercepted by `patchFetch`. */
const cartRegex = /\/cart\/(add|change|update)(\.js|\.json)?(\?.*)?$/;

/**
 * Indexes cart lines by a stable identity for diffing.
 *
 * Shopify's line `key` is `{variant_id}:{md5(line properties)}`. In this store
 * the properties hash is rewritten on every quantity change, so `key` is NOT
 * stable across mutations — diffing on it makes one line look like a removal of
 * the old key plus an add of the new key. We anchor on the variant `id` (stable
 * in both the Liquid seed and `/cart.js`) and disambiguate duplicate-variant
 * lines by their occurrence order.
 *
 * @param {{ id?: number, key?: string }[]} items
 * @returns {Map<string, Record<string, unknown>>}
 */
function indexCartLines(items) {
    const map = new Map();
    const counts = new Map();
    for (const line of items || []) {
        const variantId = String(line?.id ?? line?.key ?? '');
        const n = counts.get(variantId) ?? 0;
        counts.set(variantId, n + 1);
        map.set(n === 0 ? variantId : `${variantId}#${n}`, line);
    }
    return map;
}

/** `sessionStorage` key holding the pre-mutation cart snapshot. */
const PENDING_CART_EVENT = 'gtm_pending_cart_event';

/** Slim cart snapshot seeded from Liquid on first paint. */
let shopifyCart = slimCartSnapshot(cfg.shopifyCart);

/** Debounce timer for coalescing rapid `/cart/*` responses. */
let cartUpdateTimeout = null;

/**
 * Resolves a Shopify template name to the GA4 page type string.
 * @param {string} value - `template.name` from Liquid
 * @returns {string}
 */
function getPageType(value) {
    return PAGE_TYPE_MAP[value] || 'other';
}

/**
 * Breakpoint label for `site.mediaquery` on pageview.
 * @returns {'small' | 'medium' | 'large'}
 */
function getCurrentBreakpoint() {
    const w = window.innerWidth;
    return w <= 600 ? 'small' : w <= 960 ? 'medium' : 'large';
}

/**
 * Converts Shopify integer cents to a major-unit float with two decimals.
 * @param {number} price - Price in cents
 * @returns {number}
 */
function formatPrice(price) {
    return +(price / 100).toFixed(2);
}

/**
 * Human-readable variant label from cart line or static GTM JSON.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getItemVariantLabel(item) {
    if (typeof item?.item_variant === 'string' && item.item_variant) return item.item_variant;
    const opts = item?.options_with_values;
    if (Array.isArray(opts) && opts.length) {
        return opts.map((o) => o.value).filter(Boolean).join(' / ');
    }
    if (Array.isArray(item?.variant_options) && item.variant_options.length) {
        return item.variant_options.join(' / ');
    }
    const variantTitle = item?.variant_title;
    return typeof variantTitle === 'string' ? variantTitle : '';
}

/**
 * Normalizes Shopify discount allocation objects (line-level vs cart-level).
 * @param {Record<string, unknown>} allocation
 * @returns {Record<string, unknown>}
 */
function resolveAllocation(allocation) {
    return allocation?.discount_application ?? allocation ?? {};
}

/**
 * Comma-separated discount codes applied to a cart line, or `-` when none.
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
function getLineItemCoupon(item) {
    const codes = (item?.line_level_discount_allocations || [])
        .map(resolveAllocation)
        .filter((d) => d?.type === 'discount_code' && d?.title)
        .map((d) => d.title);
    return codes.length ? [...new Set(codes)].join(', ') : '-';
}

/**
 * `item_id` for a cart line: the `custom.sku` product metafield.
 *
 * Metafields are unreadable from JS, so the add-to-cart forms attach the value as
 * a hidden `_sku` line item property (`blocks/buy-buttons.liquid`,
 * `snippets/quick-add.liquid`); an empty metafield posts the literal string
 * `"false"`, so discard that. Mirrors `snippets/gtm-cart-line.liquid`: no fallback
 * to `variant.sku` or the product id — a product without the metafield reports an
 * empty `item_id` rather than a different kind of identifier.
 *
 * `/cart.js` reports `properties: null` for a line that has none, so read it
 * defensively rather than assuming the object is there.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {string}
 */
function getLineSku(item) {
    const sku = item?.properties?._sku;
    return typeof sku === 'string' && sku !== 'false' ? sku : '';
}

/**
 * `item_color` from the `_color` line item property — fallback for lines with no
 * Liquid-rendered GTM JSON on the page (e.g. the cart drawer). Same `"false"`
 * blank-value quirk as `getLineSku`.
 *
 * @param {{ properties?: Record<string, string> | null }} item
 * @returns {string}
 */
function getLineColor(item) {
    const color = item?.properties?._color;
    return typeof color === 'string' && color !== 'false' ? color : '';
}

/**
 * Discount amount, original unit price, and discount percentage for a cart line.
 * @param {Record<string, unknown>} item
 * @returns {{ discount: number, original_price: number, discount_percentage: number }}
 */
function getLineDiscountFields(item) {
    const finalCents = item?.final_price ?? item?.discounted_price ?? item?.price ?? 0;
    const originalCents = Math.max(
        finalCents,
        item?.compare_at_price ?? 0,
        item?.original_unit_price ?? 0,
        item?.original_price ?? 0,
    );
    const discountCents = originalCents - finalCents;
    return {
        discount: formatPrice(discountCents),
        original_price: formatPrice(originalCents),
        discount_percentage: originalCents > 0 ? Math.round((discountCents / originalCents) * 100) : 0,
    };
}

function getVariantId(item) {
    return getLineSku(item) + '-' + getItemVariantLabel(item);
}

/**
 * Cart or static product JSON → full GA4 `items[]` object.
 * @param {Record<string, unknown>} item
 * @param {number} [index]
 * @param {{ item_list_id?: string, item_list_name?: string }} [listContext]
 * @returns {Record<string, unknown>}
 */
function normalizeGtmItem(item, index = 0, listContext = CART_LIST_CONTEXT) {
    if (!item) return {};
    const normalized = {
        ...getLineDiscountFields(item),
        item_name: item.item_name || item.product_title || item.title,
        item_id: getLineSku(item),
        affiliation: GTM_AFFILIATION,
        coupon: item.coupon ?? getLineItemCoupon(item),
        price: formatPrice(item.final_price ?? item.price),
        item_variant: getItemVariantLabel(item),
        variant_id: getVariantId(item) || '',
        item_color: item.item_color || getLineColor(item),
        ean: item.sku,
        item_brand: item.item_brand || item.vendor || '',
        item_list_id: item.item_list_id || listContext.item_list_id || CART_LIST_CONTEXT.item_list_id,
        item_list_name: item.item_list_name || listContext.item_list_name || CART_LIST_CONTEXT.item_list_name,
        index,
        quantity: item.quantity ?? 1,
        stock: Boolean(item.stock ?? item.variant_inventory_quantity ?? false),
        category_list: [...new Set(item.category_list || [])]
    };
    (normalized.category_list || []).slice(0, 5).forEach((title, i) => {
        normalized[i === 0 ? 'item_category' : `item_category${i + 1}`] = title;
    });
    return normalized;
}

/**
 * `value` for the cart: total of all items (major units, tax-inclusive).
 * @param {Record<string, unknown>} cart
 * @returns {number}
 */
function getCartValue(cart) {
    return formatPrice(cart?.total_price ?? cart?.value ?? 0);
}

/**
 * Freezes the variant-accurate `#gtm-cart-line-*` JSON onto a cart line.
 *
 * The cart page renders a per-line GTM JSON node carrying fields `/cart.js`
 * omits (brand, variant label, stock, discount base, categories, coupon). We
 * copy those onto the line so they survive both the `sessionStorage` round-trip
 * and the `morphSection` cart re-render — which removes the node for a deleted
 * line before the debounced diff reads it, leaving `remove_from_cart` blank.
 *
 * No-op off the cart page (no node) or once a line is already enriched.
 *
 * @param {Record<string, unknown>} line
 * @returns {Record<string, unknown>}
 */
function enrichCartLine(line) {
    if (!line || line.__gtmEnriched) return line;
    const data = getGtmCartLineData(line);
    if (!data) return line;
    return {
        ...line,
        __gtmEnriched: true,
        item_name: data.item_name ?? line.item_name,
        item_brand: data.item_brand,
        item_variant: data.item_variant,
        variant_id: data.variant_id,
        item_color: data.item_color,
        ean: data.sku,
        coupon: data.coupon,
        original_price: data.original_price ?? line.original_price,
        stock: data.stock,
        category_list: data.category_list,
        item_list_id: data.item_list_id,
        item_list_name: data.item_list_name,
    };
}

/**
 * Minimal cart state kept in memory and `sessionStorage` for diffing.
 * @param {Record<string, unknown> | null | undefined} cart
 * @returns {{ currency?: string, total_price?: number, items_subtotal_price?: number, item_count?: number, items: unknown[] }}
 */
function slimCartSnapshot(cart) {
    if (!cart) return {items: []};
    return {
        currency: cart.currency,
        total_price: cart.total_price,
        items_subtotal_price: cart.items_subtotal_price,
        item_count: cart.item_count,
        items: (cart.items || []).map((line) => {
            const l = enrichCartLine(line);
            return {
                key: l.key,
                id: l.id,
                product_id: l.product_id,
                sku: l.sku || '',
                // A fully removed line is only ever read back from this snapshot,
                // never from a live /cart.js response, so the properties backing
                // item_id / item_color have to survive here. Only those are kept.
                properties: {_sku: getLineSku(l), _color: getLineColor(l)},
                quantity: l.quantity,
                final_price: l.final_price ?? l.price,
                variant_title: l.variant_title ?? '',
                // Cart-page-only GTM fields frozen from `#gtm-cart-line-*` so a
                // removed line keeps them after its node is morphed away.
                __gtmEnriched: l.__gtmEnriched,
                item_name: l.item_name,
                item_brand: l.item_brand,
                item_variant: l.item_variant,
                variant_id: l.variant_id,
                item_color: l.item_color,
                ean: l.sku,
                coupon: l.coupon,
                original_price: l.original_price,
                stock: l.stock,
                category_list: l.category_list,
                item_list_id: l.item_list_id,
                item_list_name: l.item_list_name,
            };
        }),
    };
}

/**
 * Parses pre-rendered GTM JSON from a DOM node (typically `<script type="application/json">`).
 * @param {Element | null} el
 * @param {string} [warnLabel] - Context for console.warn on parse failure
 * @returns {Record<string, unknown> | null}
 */
function parseGtmJsonElement(el, warnLabel = 'GTM JSON') {
    if (!(el instanceof Element)) return null;
    try {
        const raw = (el.textContent || '').trim();
        if (!raw || /Liquid error/i.test(raw)) return null;
        return JSON.parse(raw);
    } catch (e) {
        console.warn(`[GTM] Invalid JSON in ${warnLabel}`, e);
        return null;
    }
}

/**
 * Reads pre-rendered GTM JSON from a hidden `.gtm-card-product` node or `#gtm-product-{id}`.
 *
 * @param {Node | number | string} product - DOM node, or product id for lookup
 * @returns {Record<string, unknown> | null}
 */
function getGtmData(product) {
    let node = null;
    if (product instanceof Node) {
        node = product.querySelector('.gtm-card-product');
    } else if (typeof product === 'number' || typeof product === 'string') {
        node = document.querySelector(`#gtm-product-${product}`);
    }
    if (!node) return null;
    return parseGtmJsonElement(node, '.gtm-card-product');
}

/**
 * Single cart mutation line for `ecommerce.items` on add/remove events.
 *
 * @param {Record<string, unknown>} line
 * @param {number} index
 * @param {number} [quantity] - Mutation quantity (may differ from line.quantity)
 * @returns {Record<string, unknown>}
 */
function normalizeCartLine(line, index, quantity) {
    const staticGtmData = getGtmData(line.product_id) || getGtmData(line.id) || {};
    const listContext = {
        item_list_id: staticGtmData.item_list_id || CART_LIST_CONTEXT.item_list_id,
        item_list_name: staticGtmData.item_list_name || CART_LIST_CONTEXT.item_list_name,
    };
    // The static GTM JSON (#gtm-product-{id}) is rendered once for the default
    // variant, so its variant-identity fields (item_variant, sku) are stale after
    // a size change. The live /cart.js line always carries the selected variant,
    // so drop those static fields and keep only its product-level context (name,
    // brand, categories, list context). item_id is product-level and survives, but
    // normalizeGtmItem sources it from the line's `_sku` property regardless.
    const {item_variant: _iv, sku: _sku, ...productStatic} = staticGtmData;
    const payload = quantity != null ? {...productStatic, ...line, quantity} : {...productStatic, ...line};
    return normalizeGtmItem(payload, index, listContext);
}

/** Clears the previous `ecommerce` object before pushing a new GA4 ecommerce event. */
function resetEcommerce() {
    window.dataLayer.push({ecommerce: null});
}

/**
 * Pushes `text_message` when theme surfaces a user-visible message.
 * @param {unknown} message
 */
function pushTextMessage(message) {
    const text = typeof message === 'string' ? message.trim() : '';
    if (!text) return;
    window.dataLayer.push({event: 'text_message', message_text: text});
}

/**
 * Newsletter success/error events from `theme:newsletter_*` custom events.
 *
 * @param {'newsletter_subscription' | 'newsletter_error'} eventName
 * @param {{ form_id?: string, email?: string }} [detail]
 */
function pushNewsletterEvent(eventName, detail) {
    const {form_id: formId, email} = detail || {};
    if (!formId) return;
    if (eventName === 'newsletter_subscription' && !email) return;
    const payload = {event: eventName, form_id: formId};
    if (email) payload.email = email;
    window.dataLayer.push(payload);
}

/**
 * Wraps `window.fetch` to detect cart mutations and schedule a `/cart.js` refresh.
 */
function patchFetch() {
    const previousFetch = window.fetch;
    window.fetch = async function (...args) {
        const [resource] = args;
        try {
            if (typeof resource !== 'string') return previousFetch.apply(this, args);
            if (cartRegex.test(resource)) {
                const response = await previousFetch.apply(this, args);
                if (response.ok) {
                    sessionStorage.setItem(PENDING_CART_EVENT, JSON.stringify(slimCartSnapshot(shopifyCart)));
                    monitorCartUpdate();
                } else {
                    console.warn(`[GTM] Fetch ${resource} failed: ${response.status}`);
                }
                return response;
            }
        } catch (error) {
            console.error('[GTM] Fetch Wrapper Error:', error);
        }
        return previousFetch.apply(this, args);
    };
}

/** Debounced cart refresh after a mutation response. */
function monitorCartUpdate() {
    if (cartUpdateTimeout) clearTimeout(cartUpdateTimeout);
    cartUpdateTimeout = setTimeout(fetchCartData, 200);
}

/** Fetches full cart JSON and diffs against the pending snapshot. */
async function fetchCartData() {
    try {
        const response = await fetch('/cart.js?adw=1');
        if (response.ok) {
            compareCartData(await response.json());
        } else {
            console.error('[GTM] Failed to fetch /cart.js:', response.status);
        }
    } catch (error) {
        console.error('[GTM] Error fetching /cart.js:', error);
    }
}

/**
 * Compares old and new cart lines and fires `add_to_cart` / `remove_from_cart`.
 *
 * @param {Record<string, unknown>} newCartData
 */
function compareCartData(newCartData) {

    const pendingSnapshot = sessionStorage.getItem(PENDING_CART_EVENT);
    const oldCartData = pendingSnapshot ? JSON.parse(pendingSnapshot) : {items: []};
    newCartData = newCartData || {items: []};
    // Surviving lines still have their `#gtm-cart-line-*` node in the morphed
    // DOM; freeze the rich fields so add/quantity-increase events match removals.
    newCartData.items = (newCartData.items || []).map(enrichCartLine);
    const oldItems = indexCartLines(oldCartData.items);
    const newItems = indexCartLines(newCartData.items);
    newItems.forEach((newItem, key) => {
        const oldItem = oldItems.get(key);
        if (!oldItem) {
            pushCartMutationEvent('add_to_cart', newItem.quantity, newItem, newCartData);
        } else if (newItem.quantity > oldItem.quantity) {
            pushCartMutationEvent('add_to_cart', newItem.quantity - oldItem.quantity, newItem, newCartData);
        }
    });

    oldItems.forEach((oldItem, key) => {
        const newItem = newItems.get(key);
        if (!newItem) {
            pushCartMutationEvent('remove_from_cart', oldItem.quantity, oldItem, newCartData);
        } else if (oldItem.quantity > newItem.quantity) {
            pushCartMutationEvent('remove_from_cart', oldItem.quantity - newItem.quantity, newItem, newCartData);
        }
    });

    sessionStorage.removeItem(PENDING_CART_EVENT);
    shopifyCart = slimCartSnapshot(newCartData);
}

/**
 * Pushes add/remove with one GA4 item and full CDP cart snapshot.
 *
 * @param {'add_to_cart' | 'remove_from_cart'} eventName
 * @param {number} quantity
 * @param {Record<string, unknown>} item
 * @param {Record<string, unknown>} cartState
 */
function pushCartMutationEvent(eventName, quantity, item, cartState) {
    const mutationItem = normalizeCartLine(item, 0, quantity);
    const unitPrice = mutationItem.price ?? formatPrice(item.final_price ?? item.price);
    resetEcommerce();
    window.dataLayer.push({
        event: eventName,
        ecommerce: {
            currency: cartState?.currency || CURRENCY,
            value: +(unitPrice * quantity).toFixed(2),
            items: [mutationItem],
        },
    });
}

/**
 * Collapses whitespace in element text for navigation labels.
 * @param {Element | null | undefined} el
 * @returns {string}
 */
function trimText(el) {
    return (el?.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Best-effort label for buttons and footer links (`navigation_text`, popups).
 * @param {Element} el
 * @returns {string}
 */
function getElText(el) {
    return (
        trimText(el) ||
        el.dataset.navText ||
        el.getAttribute('aria-label') ||
        el.getAttribute('title') ||
        el.querySelector('[data-content]')?.dataset.content ||
        el.getAttribute('class') ||
        trimText(el.parentNode)
    );
}

/**
 * Stable popup identifier for dialog and overlay events.
 * @param {Element} dialog
 * @returns {string}
 */
function getDialogId(dialog) {
    return dialog.getAttribute('aria-labelledby') || dialog.getAttribute('class');
}

/**
 * @param {'popup_visible' | 'popup_close' | 'popup_cta'} eventType
 * @param {string} id
 */
function setDataLayerDialog(eventType, id) {
    window.dataLayer.push({event: eventType, popup_id: id});
}

/**
 * @param {'popup_visible'} eventType
 * @param {string} id
 */
function setDataLayerPopUp(eventType, id) {
    if (id) window.dataLayer.push({event: eventType, popup_id: id});
}

/**
 * @param {Element | null} popUp
 * @returns {boolean}
 */
function isVisualPopUp(popUp) {
    if (!popUp) return false;
    const cs = window.getComputedStyle(popUp);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
}

/**
 * Resolves an id for cookie banner, quick-add, or theme dialogs.
 * @param {Element} popUp
 * @returns {string}
 */
function getIdPopUp(popUp) {
    return (
        popUp.id ||
        popUp.closest('[id]')?.id ||
        popUp.querySelector('form[data-id]')?.dataset.id ||
        popUp.querySelector('form[data-testid]')?.dataset.testid ||
        popUp.getAttribute('class') ||
        'unknown_popup'
    );
}

/** Tags buttons inside a dialog so global click handler can emit `popup_cta`. */
function markButtons(popUp) {
    popUp.querySelectorAll(BUTTON_SELECTORS).forEach((el) => {
        el.dataset.layerPopupCta = getDialogId(popUp);
    });
}

/**
 * Waits for an element via `MutationObserver`. Optional `onChange` runs once on attribute change, then disconnects.
 *
 * @param {string | Node} selector
 * @param {{ root?: Element, onChange?: (el: Element) => void }} [options]
 * @returns {Promise<Element>}
 */
function observerHandler(selector, {root = document, onChange} = {}) {
    let visitedEl = false;
    return new Promise((resolve) => {
        let resolved = false;
        const predicate = selector instanceof Node ? () => selector : () => root.querySelector(selector);

        const finish = (el) => {
            if (resolved) return;
            resolved = true;
            try {
                domObserver.disconnect();
            } catch {
                /* observer may already be disconnected */
            }
            if (typeof onChange === 'function') {
                const attrObserver = new MutationObserver(() => {
                    if (visitedEl) return;
                    visitedEl = true;
                    onChange(el);
                    attrObserver.disconnect();
                });
                attrObserver.observe(el, {
                    attributes: true,
                    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
                });
            }
            resolve(el);
        };

        const immediate = predicate();
        if (immediate) return finish(immediate);

        const domObserver = new MutationObserver(() => {
            const el = predicate();
            if (el) finish(el);
        });
        domObserver.observe(root, {childList: true, subtree: true});
    });
}

/** Pushes initial `pageview` with user, site, and page context from `GTMConfig`. */
function pushDataLayerEvents() {
    const {customer, site} = cfg;
    const user = {
        logged_in: customer.loggedIn,
        type: customer.type,
    };

    if (customer.loggedIn) {
        user.account_id = customerIds.accountId;
    }

    window.dataLayer.push({
        event: 'pageview',
        user,
        application: {
            id: 'main',
            store: site.store,
            version: '3.4.0',
        },
        site: {
            language: site.language,
            environment: site.environment,
            mediaquery: getCurrentBreakpoint(),
        },
        page: {
            type: getPageType(PAGE_TYPE),
            load: document.referrer ? 'navigate' : 'direct'
        },
    });
}

/** Cart errors, theme messages, validation, and newsletter custom events. */
function errorEventHandler() {
    document.addEventListener('cart:error', (e) => {
        pushTextMessage(e?.detail?.data?.message || 'Error occurred');
    });
    document.addEventListener('theme:text_message', (e) => {
        pushTextMessage(e?.detail?.message);
    });
    document.addEventListener('theme:validation_message', (e) => {
        const text = typeof e?.detail?.validation_text === 'string' ? e.detail.validation_text.trim() : '';
        if (!text) return;
        window.dataLayer.push({
            event: 'validation_message',
            validation_text: text,
            validation_field: e.detail.validation_field || '',
        });
    });
    document.addEventListener('theme:newsletter_subscription', (e) => pushNewsletterEvent('newsletter_subscription', e?.detail));
}

/** Global `button_click` and `popup_cta` from elements marked in open dialogs. */
function generalButtonsClick() {
    document.addEventListener('click', (e) => {
        const el = e.target.closest(BUTTON_SELECTORS.join(', '));
        if (!el) return;
        if (el.dataset?.layerPopupCta) setDataLayerDialog('popup_cta', el.dataset.layerPopupCta);
        window.dataLayer.push({event: 'button_click', navigation_text: getElText(el)});
    });
}

function navigationListeners() {
    document.addEventListener('navigation:use', ({detail}) => {
        const main = detail?.maincategory?.trim();
        if (!main) return;
        const sub = detail.subcategory?.trim();
        const subsub = detail.subsubcategory?.trim();
        // Measurement plan expects the clicked label plus its menu depth.
        const navigation_text = subsub || sub || main;
        const navigation_level = subsub ? 3 : sub ? 2 : 1;
        window.dataLayer.push({
            event: 'navigation_use',
            navigation_text,
            navigation_level,
        });
    });
}

/** Minimum visible ratio before `view_promotion` fires for a hero banner. */
const VIEW_PROMOTION_THRESHOLD = 0.5;

/**
 * Parses promotion config from a hero root element.
 * @param {Element} root
 * @returns {{ creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string } | null}
 */
function parsePromotionConfig(root) {
    const script = root.querySelector('script[data-gtm-promotion-config]');
    if (!(script instanceof HTMLScriptElement)) return null;

    let data;
    try {
        data = JSON.parse(script.textContent || '{}');
    } catch {
        return null;
    }

    const promotion_id = (data.promotion_id || '').trim();
    const promotion_name = (data.promotion_name || '').trim();
    if (!promotion_id || !promotion_name) return null;

    return {
        creative_name: (data.creative_name || '').trim(),
        creative_slot: (data.creative_slot || '').trim(),
        promotion_id,
        promotion_name,
    };
}

/**
 * @returns {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>}
 */
function buildHeroPromotionCache() {
    /** @type {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} */
    const promotionCache = new Map();

    for (const target of document.querySelectorAll('[data-gtm-view-promotion]')) {
        const promotion = parsePromotionConfig(target);
        if (promotion) promotionCache.set(target, promotion);
    }

    return promotionCache;
}

/**
 * @param {'view_promotion' | 'select_promotion'} eventName
 * @param {{ creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }} promotion
 */
function pushPromotionEvent(eventName, promotion) {
    resetEcommerce();
    window.dataLayer.push({event: eventName, ecommerce: promotion});
}

/**
 * @param {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} promotionCache
 */
function initViewPromotionObserver(promotionCache) {
    let remaining = promotionCache.size;

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;

                const root = entry.target;
                if (!(root instanceof Element)) continue;

                observer.unobserve(root);
                remaining -= 1;

                const promotion = promotionCache.get(root);
                if (promotion && root.dataset.gtmPromotionFired !== 'true') {
                    root.dataset.gtmPromotionFired = 'true';
                    pushPromotionEvent('view_promotion', promotion);
                }

                if (remaining === 0) observer.disconnect();
            }
        },
        {threshold: VIEW_PROMOTION_THRESHOLD},
    );

    promotionCache.forEach((_, target) => observer.observe(target));
}

/**
 * @param {Map<Element, { creative_name: string, creative_slot: string, promotion_id: string, promotion_name: string }>} promotionCache
 */
function initSelectPromotionListener(promotionCache) {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-gtm-view-promotion] a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;

        const root = link.closest('[data-gtm-view-promotion]');
        const promotion = root ? promotionCache.get(root) : undefined;
        if (!promotion) return;

        pushPromotionEvent('select_promotion', promotion);
    });
}

/** Hero `view_promotion` (viewport) and `select_promotion` (link click). */
function initHeroPromotionTracking() {
    const promotionCache = buildHeroPromotionCache();
    if (!promotionCache.size) return;

    initViewPromotionObserver(promotionCache);
    initSelectPromotionListener(promotionCache);
}

/** `navigation_tile` on PLP carousel tile clicks (categories from header menu). */
function initNavigationTileListener() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.resource-list__carousel a.collection-card__link[href]');
        if (!link) return;

        const handle = (url = '') => (String(url).match(/\/collections\/([^/?#]+)/i) || [])[1] || '';

        const titlesFor = (items, target, trail = []) => {
            for (const item of items || []) {
                const next = [...trail, item.title?.trim() || ''];
                if (handle(item.url) === target) return next;
                const found = titlesFor(item.items, target, next);
                if (found.length) return found;
            }
            return [];
        };

        let menu = {items: []};
        try {
            menu = JSON.parse(
                document.querySelector('script[data-header-menu-hierarchy]')?.textContent || '{}',
            );
        } catch {
            console.warn('[GTM] Invalid header menu JSON');
        }

        const [maincategory, subcategory = ''] = titlesFor(menu.items, handle(link.href));
        if (!maincategory) return;

        const subsubcategory = link.querySelector('.visually-hidden')?.textContent?.trim() || '';

        window.dataLayer.push({event: 'navigation_tile', maincategory, subcategory, subsubcategory});
    });
}

/** `countrypicker_open` / `countrypicker_click` from localization components. */
function countryPickerListeners() {
    document.addEventListener('countrypicker:open', () => {
        window.dataLayer.push({event: 'countrypicker_open'});
    });
    document.addEventListener('countrypicker:click', ({detail}) => {
        const country = detail?.country?.trim();
        if (country) window.dataLayer.push({event: 'countrypicker_click', country});
    });
}

/**
 * True for a Shopify contact form (`{% form 'contact' %}`), as rendered on
 * `/pages/contact`. Detected by the hidden `form_type=contact` input Shopify
 * injects, with theme class / action fallbacks.
 * @param {HTMLFormElement | null | undefined} form
 * @returns {boolean}
 */
function isContactForm(form) {
    if (!form) return false;
    return Boolean(
        form.querySelector('input[name="form_type"][value="contact"]') ||
            form.classList.contains('contact-form__form') ||
            /\/contact(\/|$|\?|#)/.test(form.getAttribute('action') || ''),
    );
}

/** `contact` from form submit or mailto/tel/contact-page links. */
function contactListeners() {
    document.addEventListener('click', (e) => {
        const submitBtn = e.target.closest('button[type="submit"], input[type="submit"]');
        if (submitBtn) {
            const form = submitBtn.closest('form');
            if (isContactForm(form) && form.checkValidity()) {
                window.dataLayer.push({event: 'contact', platform: 'form'});
            }
            return;
        }
        const link = e.target.closest('a[href]');
        if (!link) return;
        const href = (link.getAttribute('href') || '').toLowerCase();
        const platform =
            (link.dataset.contactPlatform || '').trim().toLowerCase() ||
            (href.startsWith('mailto:') ? 'email' : '') ||
            (href.startsWith('tel:') ? 'phone' : '') ||
            (href.includes('/pages/contact') || /\/contact(\/|$|\?|#)/.test(href) ? 'contact_page' : '');
        if (platform) window.dataLayer.push({event: 'contact', platform});
    });
}

/** `footer_click` for footer anchor clicks. */
function footerNavigationClickListener() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('footer a[href]');
        if (!link) return;
        const navigation_text = getElText(link);
        if (navigation_text) window.dataLayer.push({event: 'footer_click', navigation_text});
    });
}

/** Observes cookie banner and Voyado quick-add for `popup_visible`. */
function popUpListener() {
    observerHandler('#shopify-pc__banner').then((el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });
    observerHandler('.v-quick-add-modal-dialog').then((el) => {
        if (isVisualPopUp(el)) setDataLayerPopUp('popup_visible', getIdPopUp(el));
    });
}

/**
 * Applies major-unit prices on static PDP item JSON before `view_item`.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
function formatItemPrices(item) {
    const { compare_at_price: _cap, ...rest } = item;
    const price = formatPrice(rest.price);
    const original_price = formatPrice(rest.original_price);
    return {
        ...rest,
        price,
        original_price,
        discount: Math.max(0, +(original_price - price).toFixed(2)),
    };
}

/**
 * Patches the static PDP item with the shopper's selected variant.
 *
 * The static GTM JSON (`#gtm-product-{id}`) is rendered once outside the
 * variant picker's DOM swap, so it always reflects the initial variant. This
 * overlays the live variant's price, label, and stock from a `variant:update`
 * `detail.resource`, while product-level fields (name, brand, categories, list
 * context, `item_id`) carry over from `baseItem`.
 *
 * @param {Record<string, unknown>} baseItem - Item from `formatItemPrices(getGtmData(...))`
 * @param {{ price?: number, compare_at_price?: number, options?: string[], title?: string, available?: boolean }} variant - `variant:update` `detail.resource`
 * @returns {Record<string, unknown>}
 */
function buildVariantItem(baseItem, variant) {
    const price = formatPrice(variant.price ?? 0);
    const original_price = formatPrice(Math.max(variant.compare_at_price ?? 0, variant.price ?? 0));
    const optionLabel = Array.isArray(variant.options) && variant.options.length
        ? variant.options.filter(Boolean).join(' / ')
        : typeof variant.title === 'string'
        ? variant.title
        : '';
    return {
        ...baseItem,
        // No item_id override: it is the parent-level `custom.sku` metafield, which
        // is the same for every variant. Overlaying `variant.sku` here reported a
        // variant-scoped id that no other event uses.
        price,
        original_price,
        discount: Math.max(0, +(original_price - price).toFixed(2)),
        item_variant: optionLabel || baseItem.item_variant,
        stock: Boolean(variant.available ?? baseItem.stock),
    };
}

/**
 * `add_to_wishlist` on PDP Swym button clicks (add state only).
 *
 * The Swym button toggles between `atw-button-add` and `atw-button-added`; the
 * `class*=` selector matches both, so we read the pre-click state in the capture
 * phase (before Swym toggles it) and skip clicks that are removals.
 *
 * The item is read lazily at click time so it reflects the variant the shopper
 * currently has selected (e.g. size 32/34), not the one shown on page load.
 *
 * @param {() => (Record<string, unknown> | null)} getWishlistItem - Returns the current GA4 item
 */
function initWishlistTracking(getWishlistItem) {
    document.addEventListener(
        'click',
        (e) => {
            const button = e.target.closest(".product-information button[class*='atw-button-add']");
            if (!button || button.classList.contains('atw-button-added')) return;
            const wishlistItem = getWishlistItem();
            resetEcommerce();
            window.dataLayer.push({
                event: 'add_to_wishlist',
                ecommerce: {
                    currency: CURRENCY,
                    value: wishlistItem?.price ?? 0,
                    items: wishlistItem ? [wishlistItem] : [],
                },
            });
        },
        true,
    );
}

/**
 * PDP-only: `view_item`, variant change, gallery, reviews
 * Only registered when `GTMConfig.pageType === 'product'`.
 */
function initProductPageTracking() {
    const productId = cfg.productId;

    const gtmData = getGtmData(productId);
    const baseItem = gtmData ? formatItemPrices(gtmData) : null;
    /** Live PDP item, re-patched on each `variant:update` to track the selected variant. */
    let currentItem = baseItem;

    if (baseItem) {
        resetEcommerce();
        window.dataLayer.push({
            event: 'view_item',
            ecommerce: {currency: CURRENCY, value: baseItem.price, items: [baseItem]},
        });
    }

    initWishlistTracking(() => currentItem);

    // Dispatched by assets/voyado-reviews.js when the inline mini rating is
    // clicked/activated to scroll to the full reviews section.
    document.addEventListener('theme:review_click', () => window.dataLayer.push({event: 'review_click'}));

    document.addEventListener('variant:update', (e) => {
        const resource = e?.detail?.resource;
        if (!resource || !baseItem) return;
        // Combined-listing swaps load a different product; base product-level
        // fields (name, categories) would be stale, so skip patching.
        if (e.detail?.data?.newProduct) return;
        currentItem = buildVariantItem(baseItem, resource);
    });

    document.addEventListener('variant:selected', (e) => {
        const {id} = e?.detail?.resource || {};
        if (!id) return;
        resetEcommerce();
        window.dataLayer.push({event: 'change_variant', product: id});
    });

    let lastPhotoZoomAt = 0;
    let lastPhotoZoomIndex = -1;

    const setDataLayerMediaGallery = (eventType, index) => {
        window.dataLayer.push({event: eventType, product: productId, index});
    };

    const pushPhotoZoom = (index) => {
        const now = Date.now();
        if (index === lastPhotoZoomIndex && now - lastPhotoZoomAt < 400) return;
        lastPhotoZoomIndex = index;
        lastPhotoZoomAt = now;
        setDataLayerMediaGallery('photo_zoom', index);
    };

    // Zoom dialog media tracking. Opening the dialog and every in-zoom change
    // emit overlapping signals for the SAME photo change:
    //   - `zoom-media:index`     -> once, on zoom-in
    //   - `zoom-media:thumbnail` -> on a thumbnail click
    //   - `zoom-media:selected`  -> on EVERY change: scroll, swipe, AND as a side
    //                               effect of both the zoom-in and each thumbnail
    //                               click (plus the main gallery echoes it back as
    //                               a no-`event` `slideshow:select`).
    // Mapping each signal directly double-fired (thumbnail click => 2x photo_select)
    // and left scrolls untracked. To emit exactly ONE event per interaction we
    // defer the `selected` signal briefly: a `thumbnail` or `index` event arriving
    // in the same tick reclassifies it (photo_select / photo_zoom) and cancels the
    // deferred scroll; a lone `selected` is a genuine scroll/swipe -> photo_scroll.
    let lastZoomIndex = -1;
    let pendingScrollIndex = -1;
    let zoomScrollTimer = null;

    const cancelPendingZoomScroll = () => {
        if (zoomScrollTimer) clearTimeout(zoomScrollTimer);
        zoomScrollTimer = null;
        pendingScrollIndex = -1;
    };

    const flushZoomScroll = () => {
        zoomScrollTimer = null;
        const i = pendingScrollIndex;
        pendingScrollIndex = -1;
        if (i < 0 || i === lastZoomIndex) return; // same photo already reported
        lastZoomIndex = i;
        setDataLayerMediaGallery('photo_scroll', i + 1);
    };

    document.addEventListener('zoom-media:index', (e) => {
        cancelPendingZoomScroll(); // the zoom-in's own `selected` is not a scroll
        lastZoomIndex = e.detail.index;
        pushPhotoZoom(e.detail.index + 1);
    });

    document.addEventListener('zoom-media:thumbnail', (e) => {
        cancelPendingZoomScroll(); // a thumbnail click is a select, not a scroll
        lastZoomIndex = e.detail.index;
        setDataLayerMediaGallery('photo_select', e.detail.index + 1);
    });

    document.addEventListener('zoom-media:selected', (e) => {
        pendingScrollIndex = e.detail.index;
        if (zoomScrollTimer) clearTimeout(zoomScrollTimer);
        zoomScrollTimer = setTimeout(flushZoomScroll, 80);
    });

    document.addEventListener('slideshow:select', (e) => {
        const {slide, index, trigger, event} = e.detail;
        const i = index + 1;
        const gallery = slide?.closest('media-gallery');
        if (!gallery) return;
        const slideshow = gallery.querySelector('slideshow-component');
        const slideCount = slideshow?.slides?.length ?? 0;
        const isLastSlide = slideCount > 0 && index === slideCount - 1;

        // Classify the main-carousel navigation (mobile shows prev/next arrows;
        // desktop is a grid with none). Arrow buttons render as
        // <button ref="next|previous"> inside <slideshow-arrows>, so detect them by
        // the ref on the clicked control — checked before the controls branch so an
        // arrow is never also reported as photo_select. No-`event` selects are the
        // main gallery echoing the zoom dialog's navigation and are ignored (the
        // zoom handlers above already report those; mapping them here double-fired
        // photo_select).
        const ref = event?.target?.closest?.('[ref]')?.getAttribute('ref');
        if (trigger === 'scroll') {
            setDataLayerMediaGallery('photo_scroll', i);
        } else if (ref === 'next' || ref === 'previous') {
            setDataLayerMediaGallery(`photo_${ref}`, i);
        } else if (event?.target?.closest?.('slideshow-controls')) {
            setDataLayerMediaGallery('photo_select', i);
        }
        if (isLastSlide) setDataLayerMediaGallery('photo_last', i);
    });
}

/** Cart template: `view_cart` with `currency`, `value`, and GA4 `items[]` (same item shape as `view_item`). */
function getGtmCartLineData(line) {
    if (!line || line.product_id == null || line.id == null) return null;
    const id = `gtm-cart-line-${line.product_id}-${line.id}`;
    return parseGtmJsonElement(document.getElementById(id), id);
}

/** GA4 `items[]` for cart page `view_cart` — reads `#gtm-cart-line-*`, formats like `view_item`. */
function buildViewCartItems(cart) {
    return (cart?.items || [])
        .map((line, index) => {
            const gtmData = getGtmCartLineData(line);
            if (!gtmData) return null;
            return {...formatItemPrices(gtmData), index, quantity: line.quantity ?? 1};
        })
        .filter(Boolean);
}

function initCartPageTracking() {
    resetEcommerce();
    window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
            currency: shopifyCart?.currency || CURRENCY,
            value: getCartValue(shopifyCart),
            items: buildViewCartItems(shopifyCart),
        },
    });
}

/** Registers listeners shared on every template; branches for cart and PDP. */
function initGlobalTracking() {
    pushDataLayerEvents();
    errorEventHandler();
    generalButtonsClick();
    navigationListeners();
    countryPickerListeners();
    contactListeners();
    footerNavigationClickListener();
    popUpListener();
    initHeroPromotionTracking();

    document.addEventListener('dialog:open', (e) => {
        const {dialog} = e.detail;
        markButtons(dialog);
        setDataLayerDialog('popup_visible', getDialogId(dialog));
    });

    document.addEventListener('dialog:close', (e) => {
        setDataLayerDialog('popup_close', getDialogId(e.detail.dialog));
    });

    if (PAGE_TYPE === 'cart') initCartPageTracking();
    if (PAGE_TYPE === 'product') initProductPageTracking();
    if (PAGE_TYPE === 'collection') initNavigationTileListener();

    // Shop the Look: the Lookfy app renders its own product markup, so the GTM
    // JSON and the `_sku` / `_color` line item properties have to be supplied
    // separately. Loaded only where the app block is on the page.
    if (document.querySelector('.lookfy-gallery-block')) {
        import('./gtm-lookfy.js')
            .then(({initLookfyTracking}) => initLookfyTracking())
            .catch((error) => console.warn('[GTM] Lookfy bridge failed to load', error));
    }
}

if (sessionStorage.getItem(PENDING_CART_EVENT)) {
    fetchCartData();
}

if (!window.__GTM_patchedFetch) {
    window.__GTM_patchedFetch = true;
    patchFetch();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlobalTracking);
} else {
    initGlobalTracking();
}
